import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useLocale } from 'locale';
import RocketIcon from 'assets/svg/rocket.svg';
import { TextLabel } from 'ds/components/typography';
import { CircularProgressButton } from 'components/progrees-circle';
import { formatCountdown, useCountdown } from 'hooks/useCountdown';
import { keyframes } from 'emotion';

const AutoBumpTimerRoot = styled.div(({ theme }) => ({
  alignSelf: 'stretch',
  flexGrow: 1,
  height: '48px',
  padding: theme.spacing(0.5),
  backgroundColor: theme.colors.brand.lightBlue,
  borderRadius: theme.borderRadius.extraXLarge,
  display: 'flex',
  justifyContent: 'start',
  gap: theme.spacing(1),
  cursor: 'default',
}));

const TextGroup = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const FlipWrapper = styled.div({
  width: 40,
  height: 40,
  perspective: '1000px',
  flexShrink: 0,
});

const FlipInner = styled.div<{ animate: boolean }>(({ animate }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  transformStyle: 'preserve-3d',
  transition: 'transform 0.7s ease',
  transform: animate ? 'rotateY(180deg)' : 'rotateY(0deg)',
}));

const FlipSide = styled.div({
  position: 'absolute',
  inset: 0,
  backfaceVisibility: 'hidden',
  WebkitBackfaceVisibility: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const FlipFront = styled(FlipSide)({
  transform: 'rotateY(0deg)',
  'button': {
    cursor: 'default',
  },
});

const FlipBack = styled(FlipSide)({
  transform: 'rotateY(180deg)',
});

const pulseRing = keyframes({
  '0%': {
    transform: 'scale(0.9)',
    opacity: 0.8,
  },
  '70%': {
    transform: 'scale(1.4)',
    opacity: 0.3,
  },
  '100%': {
    transform: 'scale(1.5)',
    opacity: 0,
  },
});

const UpgradeCircle = styled.div(() => ({
  position: 'relative',
  width: 40,
  height: 40,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const UpgradePulse = styled.div(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  borderRadius: '50%',
  backgroundColor: theme.colors.brand.madlanGreen,
  animation: `${pulseRing} 1.4s ease-out infinite`,
}));

const UpgradeCore = styled.div(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: theme.colors.brand.madlanGreen,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: `0 0 0 2px ${theme.colors.neutrals.white}`,
}));

const CheckIcon = styled.div(({ theme }) => ({
  width: 12,
  height: 7,
  borderLeft: `3px solid ${theme.colors.neutrals.white}`,
  borderBottom: `3px solid ${theme.colors.neutrals.white}`,
  transform: 'rotate(-45deg) translateY(-1px)',
}));

const Timer: React.FC<{ time: string }> = ({ time }) => <TextLabel weight="bold">{time}</TextLabel>;

interface AutoBumpTimerProps {
  intervalMinutes: number;
  autoPromotionDates: string[];
}

export const AutoBumpTimer: React.FC<AutoBumpTimerProps> = props => {
  const { intervalMinutes, autoPromotionDates } = props;
  const { t } = useLocale();
  const [ animate, setAnimate ] = useState(false);

  const getNextDate = useCallback(() => {
    if (autoPromotionDates && autoPromotionDates.length > 0) {
      return autoPromotionDates.find(dateStr => new Date(dateStr) >= new Date());
    }
    return null;
  }, [ autoPromotionDates ]);

  const [ nextDate, setNextDate ] = useState<string | null>(getNextDate());

  const countdownNextDate = useMemo(() => new Date(nextDate), [ nextDate ]);
  const timeLeft = useCountdown(countdownNextDate);

  useEffect(() => {
    if (!timeLeft && !animate && nextDate) {
      setAnimate(true);
      const newDate = getNextDate();
      setNextDate(newDate);
    }
  }, [ timeLeft, animate, nextDate, setAnimate, setNextDate, getNextDate ]);

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout> = null;
    if (animate) {
      timerId = setTimeout(() => {
        setAnimate(false);
      }, 5000);
    }
    return () => clearTimeout(timerId);
  }, [ animate, setAnimate ]);

  const intervalMs = intervalMinutes * 60 * 1000;
  const progress = Math.floor((100 - ((timeLeft * 100) / intervalMs)));

  if (!timeLeft && !nextDate && !animate) return null;

  return (
    <AutoBumpTimerRoot>
      <FlipWrapper>
        <FlipInner animate={animate}>
          <FlipFront>
            <CircularProgressButton progress={progress}>
              <RocketIcon width={24} height={24} />
            </CircularProgressButton>
          </FlipFront>

          <FlipBack>
            <UpgradeCircle>
              <UpgradePulse />
              <UpgradeCore>
                <CheckIcon />
              </UpgradeCore>
            </UpgradeCircle>
          </FlipBack>
        </FlipInner>
      </FlipWrapper>

      <TextGroup>
        <TextLabel small>{t('manageBulletinPage.auto.promoto.text')}</TextLabel>
        <Timer time={formatCountdown(timeLeft)} />
      </TextGroup>
    </AutoBumpTimerRoot>
  );
};
